import MatterRequestCard from './matterRequestCard';
import { ICustomerType } from '../../constants/customer';
import { useFetchCompanyMatterRequests } from '../../hooks/useFetchCompanyMatterRequests';
import { useParams, useSearchParams } from 'react-router-dom';
import { useFetchPrivateMatterRequests } from '../../hooks/useFetchPrivateMatterRequests';
import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { IMatterRequest } from '../../interface/matterRequest';
import NoData from '../dashboard/noData';
import { ReceiveEmpty } from '../icons';
import { Spin } from '../Ant';
import { IFilterOption } from '../../interface/transaction';
import { MatterRequestStatus } from '../../constants/matter';
import { useQueryClient } from '@tanstack/react-query';
import { useFetchAllMatterRequests } from '../../hooks/useFetchAllMatterRequests';
import { useMatterRequestOwnerFilter } from '../../hooks/useMatterRequestOwnerFilter';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { MY_OWN_FILTER, ROUTES } from '../../constants/constants';
import { useRestrictedUserMatterPermissions } from '../../hooks/useRestrictedUserMatterPermissions';
import { IActiveSortState, ISortOption } from '../../interface/common';
import SortSelector from '../sortPopover/sortSelector';
import { useManageMatterRequestContext } from '../../context/ManageMatterRequest';
import SkeletonCardList from '../common/skeleton/skeletonCardList';
import { ICustomer } from '../../interface/customer';
import { showToast } from '../utils/toast';

const buildCustomerPrefill = (customer?: ICustomer): IMatterRequest | null => {
  if (!customer?.id) {
    return null;
  }
  if (customer.type === ICustomerType.COMPANY) {
    return {
      principalCompanyId: customer.id,
      principalCompany: customer,
    } as unknown as IMatterRequest;
  }
  if (customer.type === ICustomerType.PRIVATE_PERSON) {
    return {
      principalPrivatePersonId: customer.id,
      principalPrivatePerson: { ...customer, fullName: customer.name },
    } as unknown as IMatterRequest;
  }
  return null;
};
import CollapsibleListLayout from '../collapsibleListLayout/collapsibleListLayout';

const MatterRequestListing = ({
  type,
  isFromCustomer = false,
  customer,
}: {
  type?: string;
  isFromCustomer?: boolean;
  customer?: ICustomer;
}) => {
  const { t } = useTranslation();
  const { canCreateMatterRequests } = useRestrictedUserMatterPermissions();
  const { push } = useNavigationStack();
  const queryClient = useQueryClient();
  const { updateManageMatterRequest } = useManageMatterRequestContext();
  const { id: customerId } = useParams();
  const isCompany = type === ICustomerType.COMPANY;
  const matterRequestFilterOptions: IFilterOption[] = [
    { label: t('matterRequest:status.open'), value: MatterRequestStatus.open },
    { label: t('matterRequest:status.closed'), value: MatterRequestStatus.closed },
    { label: t('matterRequest:status.rejected'), value: MatterRequestStatus.rejected },
  ];
  const matterRequestSortOptions: ISortOption[] = useMemo(
    () => [
      {
        field: 'createdOn',
        label: t('matterRequest:createdOn'),
        type: 'date',
        defaultDirection: 'DESC',
      },
      { field: 'subject', label: t('matter:subject'), type: 'text', defaultDirection: 'ASC' },
      ...(isFromCustomer
        ? []
        : [
            {
              field: 'principal.name',
              label: t('matter:principal'),
              type: 'text' as const,
              defaultDirection: 'ASC' as const,
            },
          ]),
    ],
    [t, isFromCustomer],
  );

  const [activeSort, setActiveSort] = useState<IActiveSortState>({
    field: 'createdOn',
    direction: 'DESC',
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const isChildList = type !== undefined || isFromCustomer;
  const ownerFilter = useMatterRequestOwnerFilter({
    isMyOwnDefault: !isChildList,
    restoredValue: searchParams.get('matterRequestOwner'),
  });
  const initialSearch = searchParams.get('matterRequestSearch') || '';
  const [searchValue, setSearchValue] = useState<string>(initialSearch);
  const initialFilterValue = searchParams.get('matterRequestFilter') || '';
  const initialFilterValues = initialFilterValue ? initialFilterValue.split(',') : [];
  const initialSelectedFilter = matterRequestFilterOptions.filter((opt) =>
    initialFilterValues.includes(opt.value),
  );
  const [selectedFilter, setSelectedFilter] = useState<IFilterOption[]>(initialSelectedFilter);
  const defaultParams = {
    limit: 10,
    sortBy: [`${activeSort.field}:${activeSort.direction}`],
    customerId: customerId ?? '',
    search: searchValue,
    extraFilters: {
      status: selectedFilter?.length
        ? `$in:${selectedFilter.map((x) => x.value).join(',')}`
        : MatterRequestStatus.open,
      ...(isChildList ? ownerFilter.apiFilters : {}),
    },
    relations: ['parties', 'parties.company', 'parties.privatePerson', 'responsibleUser:fullName'],
    fields: ['subject', 'createdOn', 'status', 'hasImportantNotes', 'hasNotes'],
  };
  // The main list has its own endpoint for "my own"; a customer's list is filtered by parameters.
  const listParams = type
    ? defaultParams
    : { ...defaultParams, myOwn: !isChildList && ownerFilter.isMyOwn };
  const getSelectedHook = () => {
    if (!type) return useFetchAllMatterRequests;
    if (isCompany) return useFetchCompanyMatterRequests;
    return useFetchPrivateMatterRequests;
  };

  const {
    data: matterRequestList,
    isLoading: isMatterRequestLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = getSelectedHook()(listParams);
  const { ref, inView } = useInView({
    threshold: 0.5,
  });
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && matterRequestList?.length > 0) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage]);
  const renderLoader = () => {
    if (isFetching) {
      return <Spin size="small" />;
    }
    return null;
  };
  const ownerFilterLockedMessage = t('matterRequest:ownerFilterLockedMessage');
  const resetOwnerFilter = () => {
    if (ownerFilter.isLocked) {
      showToast('warning', ownerFilterLockedMessage);
      return;
    }
    ownerFilter.reset();
  };
  const handleAddMatterRequest = () => {
    updateManageMatterRequest(isFromCustomer ? buildCustomerPrefill(customer) : null);
    push(ROUTES.MANAGE_MATTER_REQUEST);
  };
  useEffect(() => {
    setSearchParams({
      matterRequestSearch: searchValue,
      matterRequestFilter: selectedFilter.map((x) => x.value).join(','),
      matterRequestOwner: ownerFilter.value,
    });
  }, [searchValue, selectedFilter, ownerFilter.value]);
  return (
    <CollapsibleListLayout
      testId="matter-request-listing"
      header={
        <SearchInputWithFilters
          searchTitle={t('matterRequest:search')}
          handleSearch={(val) => {
            setSearchValue(val);
          }}
          searchValue={searchValue}
          filterTitle={t('matterRequest:matterRequestFilters')}
          radioOptions={ownerFilter.options}
          radioTitle={ownerFilter.title}
          checkboxTitle={t('matterRequest:statusFilter')}
          checkboxOptions={matterRequestFilterOptions}
          selectedFilter={[...ownerFilter.selectedOptions, ...selectedFilter]}
          setSelectedFilter={ownerFilter.select}
          setSelectedCheckboxFilter={(val: IFilterOption[]) => {
            setSelectedFilter(val);
          }}
          handleRefresh={() => {
            queryClient.removeQueries({
              queryKey: [isCompany ? 'company-matter-requests' : 'private-matter-requests'],
            });
            return refetch();
          }}
          handleAdd={canCreateMatterRequests ? handleAddMatterRequest : undefined}
          tooltipTextForAdd={t('matterRequest:createMatterRequest')}
          disableFilter={ownerFilter.isLocked}
          filtersDisabledInfoMessage={ownerFilter.isLocked ? ownerFilterLockedMessage : undefined}
          clearFilter={(val?: string) => {
            if (val === MY_OWN_FILTER) {
              resetOwnerFilter();
              return;
            }
            if (val) {
              setSelectedFilter((prev) => prev.filter((item) => item.value !== val));
              return;
            }
            setSelectedFilter([]);
            resetOwnerFilter();
          }}
        >
          <SortSelector
            modalLabel={t('common:sorting.title')}
            options={matterRequestSortOptions}
            onApply={(sort) => setActiveSort(sort)}
            activeSort={activeSort}
          />
        </SearchInputWithFilters>
      }
    >
      {matterRequestList.map((val: IMatterRequest) => {
        return <MatterRequestCard key={val.id} matterRequest={val} />;
      })}
      {hasNextPage && <div ref={ref} className="loading-indicator" />}
      {(!matterRequestList ||
        (Object.entries(matterRequestList).length === 0 && !isMatterRequestLoading)) && (
        <div
          className="flex-grow d-flex justify-center items-center"
          role="status"
          aria-live="polite"
        >
          <NoData
            header={t('matterRequest:noMatterRequests')}
            subHeader={ownerFilter.isLocked ? ownerFilterLockedMessage : ''}
            icon={<ReceiveEmpty />}
          />
        </div>
      )}
      {isMatterRequestLoading ? <SkeletonCardList rows={[1, 1]} /> : renderLoader()}
    </CollapsibleListLayout>
  );
};

export default MatterRequestListing;
